import { useState, type ReactNode } from 'react';
import { shortCount, toggleLike, useGameStats } from '../lib/gameStats';
import { toggleFavorite, useLibrary } from '../lib/library';
import { NavIcon } from './NavIcons';
import { ControlsButton } from './ControlsButton';
import { ReportButton } from './ReportButton';

/** Thumbs-up with the game's like count. */
export function LikeButton({ gameId }: { gameId: string }) {
  const { likes, liked } = useGameStats(gameId);
  return (
    <button
      type="button"
      className={`like-btn${liked ? ' is-liked' : ''}`}
      aria-pressed={liked}
      title={liked ? 'You like this game' : 'Like this game'}
      onClick={() => toggleLike(gameId)}
    >
      <NavIcon name="like" size={18} />
      <span>{shortCount(likes)}</span>
    </button>
  );
}

/** Heart that adds the game to the homepage's Your Favorites row. */
export function FavoriteButton({ gameId }: { gameId: string }) {
  const on = useLibrary().favorites.includes(gameId);
  return (
    <button type="button" className={`like-btn bar-icon${on ? ' is-liked' : ''}`} aria-pressed={on} aria-label={on ? 'Remove from favorites' : 'Add to favorites'} title={on ? 'In your favorites' : 'Add to favorites'} onClick={() => toggleFavorite(gameId)}>
      <NavIcon name="heart" size={18} />
    </button>
  );
}

/** Copies the game's link, for sharing with a friend. */
export function ShareButton({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const url = `${location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      },
      () => {},
    );
  };
  return (
    <button type="button" className="like-btn bar-icon" aria-label="Copy link to this game" title={copied ? 'Link copied!' : 'Copy link'} onClick={copy}>
      {copied ? <span className="bar-icon__done">Copied!</span> : <NavIcon name="share" size={18} />}
    </button>
  );
}

/** The game's action buttons in the bar under it (bottom right, like the big game sites). */
export function GameActions({ gameId, path, controls }: { gameId: string; path: string; controls?: ReactNode }) {
  return (
    <span className="game-actions">
      <ControlsButton gameId={gameId}>{controls}</ControlsButton>
      <LikeButton gameId={gameId} />
      <FavoriteButton gameId={gameId} />
      <ShareButton path={path} />
      <ReportButton gameId={gameId} />
    </span>
  );
}
